import { copy } from '@ohear/shared'
/**
 * Length-variance modal — Pro Tools light panel styling.
 *
 * Fires after a take lands when its duration deviates from the reference voice
 * by more than the project's `length_variance_pct` threshold (default ±15%).
 *
 * Three actions (rendered in the parent Modal's footer):
 *   - Keep this take as the best (closes prompt; the take stays accepted)
 *   - Re-record the cue
 *   - Mark for supervisor review (placeholder)
 *
 * Tone rules (friendly_ui_tone.md):
 *   - Phrased as a question, not a directive
 *   - One issue at a time
 *   - Copy lives in `@ohear/shared` `copy.length` — never hard-coded here
 */
import { Button, Modal } from '@ohear/ui'
import { useT } from '../../lib/i18n/use-t'

export interface VariancePromptProps {
  open: boolean
  /**
   * Signed deviation as a fraction of reference duration. Positive = take is
   * longer than reference; negative = shorter. e.g. 0.22 means +22%.
   */
  variance: number
  onKeep: () => void
  onRetake: () => void
  onMarkForReview: () => void
  onClose: () => void
}

export function VariancePrompt({
  open,
  variance,
  onKeep,
  onRetake,
  onMarkForReview,
  onClose,
}: VariancePromptProps) {
  const t = useT()
  const percentSigned = variance * 100
  const summary = copy.length.variancePercentSummary(percentSigned)

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t(copy.length.VARIANCE_TITLE)}
      footer={
        <>
          <Button variant="ghost" onClick={onMarkForReview}>
            <span aria-hidden="true">{copy.length.MARK_FOR_REVIEW.icon} </span>
            {t(copy.length.MARK_FOR_REVIEW)}
          </Button>
          <Button variant="ghost" onClick={onRetake}>
            <span aria-hidden="true">{copy.length.RETAKE.icon} </span>
            {t(copy.length.RETAKE)}
          </Button>
          <Button variant="primary" onClick={onKeep}>
            <span aria-hidden="true">{copy.length.KEEP_TAKE.icon} </span>
            {t(copy.length.KEEP_TAKE)}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {/* Variance summary inside a PT yellow accent strip — reads like a PT
            warning chip. */}
        <div className="pt-ribbon flex items-center gap-2 px-3 py-2 text-[13px]">
          <span aria-hidden="true">{copy.length.VARIANCE_TITLE.icon}</span>
          <span className="pt-mono font-bold tabular-nums" aria-label={t(summary)}>
            {t(summary)}
          </span>
        </div>
        <p className="m-0 text-[13px] text-pt-text-soft">
          {t({
            zh: '挑一个最稳的处理方式 — 三种都不会丢这条 take。',
            en: 'Pick whichever feels right — none of these throws away this take.',
            es: 'Elija la opción que prefiera — ninguna descarta esta toma.',
          })}
        </p>
      </div>
    </Modal>
  )
}
